import { BaseModel } from '..';

const initialState: FlightsState = {
  filters: {},
  flightsMap: new Map(),
  aircraftFlightsMaps: new Map(),
  lastFetched: 0,
  pagination: {},
  params: {},
  ttl: 30000,
  dirty: false,
  data: {
    params: [],
    filters: [],
  },
};

export enum FlightsActionTypes {
  DELETE_FLIGHT = 'flights/delete',
  SAVE = 'flights/save',
  SAVE_ALL = 'flights/saveAll',
  SAVE_FLIGHTS_ONLY = 'flights/saveFlightsOnly',
  SAVE_FLIGHT_DETAILS = 'flights/saveFlightDetails',
  SET_PARAMS = 'flights/setParams',
  SET_DIRTY = 'flights/setDirty',
  SET_FILTERS = 'flights/setFilters',
}

export interface DeleteFlightActionTypes {
  type: FlightsActionTypes.DELETE_FLIGHT;
  payload: Flight;
}

export interface SaveFlightsActionTypes {
  type: FlightsActionTypes.SAVE;
  payload: Flight;
}

export interface SaveAllFlightsActionTypes {
  type: FlightsActionTypes.SAVE_ALL;
  payload: { list: Flight[]; pagination: {} };
}

export interface SaveFlightsOnlyFlightsActionTypes {
  type: FlightsActionTypes.SAVE_FLIGHTS_ONLY;
  payload: { flights: Flight[]; aircraftId: string };
}

export interface SaveFlightDetailsFlightsActionTypes {
  type: FlightsActionTypes.SAVE_FLIGHT_DETAILS;
  payload: Flight;
}

export interface SetParamsFlightsActionTypes {
  type: FlightsActionTypes.SET_PARAMS;
  payload: Flight;
}

export interface SetDirtyFlightsActionTypes {
  type: FlightsActionTypes.SET_DIRTY;
  payload: boolean;
}

export interface SetFiltersFlightsActionTypes {
  type: FlightsActionTypes.SET_FILTERS;
  payload: Flight;
}

type FlightsAction =
  | DeleteFlightActionTypes
  | SaveFlightsActionTypes
  | SaveAllFlightsActionTypes
  | SaveFlightsOnlyFlightsActionTypes
  | SaveFlightDetailsFlightsActionTypes
  | SetParamsFlightsActionTypes
  | SetDirtyFlightsActionTypes
  | SetFiltersFlightsActionTypes;

export default function(state = initialState, action: FlightsAction): FlightsState {
  switch (action.type) {
    case FlightsActionTypes.SAVE: {
      const flightsMap = new Map(state.flightsMap.entries());
      flightsMap.set(action.payload.id, {
        ...action.payload,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        flightsMap,
        dirty: false,
      };
    }
    case FlightsActionTypes.SAVE_ALL: {
      const flightsMap = new Map(); // @TODO: build map from previous entries and use client side filtering
      if (action.payload && 'list' in action.payload && Array.isArray(action.payload.list)) {
        action.payload.list.forEach((flight) => {
          flightsMap.set(flight.id, {
            ...flightsMap.get(flight.id),
            ...flight,
          });
        });
      }
      return {
        ...state,
        flightsMap,
        pagination: action.payload.pagination || {},
        lastFetched: Date.now(),
        dirty: false,
      };
    }
    case FlightsActionTypes.SAVE_FLIGHTS_ONLY: {
      const aircraftFlightsMaps = new Map(state.aircraftFlightsMaps.entries());
      // Check if we already have flights set for this aircraft
      const flightsMap = new Map();
      action.payload.flights.forEach((flight) => {
        flightsMap.set(flight.id, flight);
      });
      aircraftFlightsMaps.set(action.payload.aircraftId, {
        lastFetched: Date.now(),
        flightsMap,
      });
      return {
        ...state,
        aircraftFlightsMaps,
        dirty: false,
      };
    }
    case FlightsActionTypes.SAVE_FLIGHT_DETAILS: {
      const flightsMap = new Map(state.flightsMap.entries());
      flightsMap.set(action.payload.id, {
        ...flightsMap.get(action.payload.id),
        ...action.payload,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        flightsMap,
        dirty: false,
      };
    }
    case FlightsActionTypes.SET_PARAMS: {
      return {
        ...state,
        params: action.payload,
      };
    }
    case FlightsActionTypes.SET_FILTERS: {
      return {
        ...state,
        filters: action.payload,
      };
    }
    case FlightsActionTypes.SET_DIRTY: {
      return {
        ...state,
        dirty: action.payload,
      };
    }
    case FlightsActionTypes.DELETE_FLIGHT: {
      const flightsMap = new Map(state.flightsMap.entries());
      if (action.payload && action.payload.id) {
        flightsMap.delete(action.payload.id);
      }
      return {
        ...state,
        flightsMap,
        dirty: true,
      };
    }
    default:
      return state;
  }
}

export interface FlightsState {
  filters: {};
  flightsMap: Map<string, BaseFlight | Flight>;
  aircraftFlightsMaps: Map<
    string,
    {
      lastFetched: number;
      flightsMap: Map<string, BaseFlight | Flight>;
    }
  >;
  lastFetched: number;
  pagination: {};
  params: {};
  ttl: number;
  dirty: boolean;
  data: {
    params: [];
    filters: [];
  };
}

export interface BaseFlight extends BaseModel {
  id: string;
  aircraft: {
    registration: string;
    id: string;
    type: string;
  };
  aircraft_registration: string;
  arrival_airport: unknown;
  departure_airport: unknown;
  landings_count: number;
  marked_as_checked: boolean;
  sector_number: number;
  source: unknown;
  status: string;
  time_landing: string;
  time_offblocks: string;
  time_onblocks: string;
  time_takeoff: string;
  trip: {
    id: string;
    number: number;
    date: string;
    srp_number: number;
  };
}

export interface Flight extends BaseFlight {
  apu_seconds: unknown;
  arrival_airport_id: unknown;
  arrival_fuel: number;
  departure_airport_id: unknown;
  departure_fuel_adjustment: unknown;
  departure_fuel_pre_uplifts: number;
  hobbs_arrival: string;
  hobbs_departure: string;
  last_updated: number;
  remote_deice: unknown;
  pilot_flying: {
    id: string;
    first_name: string;
    last_name: string;
  };
  pilot_monitoring: {
    id: string;
    first_name: string;
    last_name: string;
  };
  trip: Trip;
  deice_events: [];
  fuel_uplifts: FuelUplift[];
  departure_fuel_log: {
    aircraft_planned_uplift: string;
    aircraft_actual_uplift: string;
    aircraft_value_post_event: string;
    aircraft_unit: string;
  };
  oil_uplift: {
    unit: unknown;
    engine_1: unknown;
    engine_2: unknown;
  };
  departure_airport_details: {
    latitude: unknown;
    longitude: unknown;
    timezone: unknown;
    proximate_to_coast: unknown;
    weather_category: unknown;
    metar: unknown;
  };
  prior_corrosion_risk_score: unknown;
  prior_corrosion_risk_details: string;
  parked_duration: unknown;
  wet_weather_percentage: number;
  arrival_airport_details: {
    latitude: unknown;
    longitude: unknown;
    timezone: unknown;
    weather_category: unknown;
    metar: unknown;
  };
  postflight_signature_url: string;
  postflight_signature_image_url: string;
  preflight_signature_url: string;
  preflight_signature_image_url: string;
  attachments: [];
  defects: [];
  versions: [];
}

export interface FuelUplift extends BaseModel {
  id: string;
  aircraft_value_post_event: unknown;
  density: unknown;
  aircraft_unit: string;
  is_deleted: false;
  truck_planned_uplift: unknown;
  truck_actual_uplift: unknown;
  aircraft_planned_uplift: string;
  aircraft_actual_uplift: string;
  truck_unit: unknown;
  attachments: [];
}

export interface Trip extends BaseModel {
  id: string;
  aircraft_id: string;
  operator_id: string;
  date: string;
  number: number;
  srp_number: number;
  srp_pages: number;
  srp_reference: string;
  flight_seconds: number;
  apu_seconds: number;
  landings: number;
  cycles: number;
  fuel_delta: number;
  trip_category: unknown;
  aircraft_registration: string;
  aircraft: {
    registration: string;
    id: string;
    type: string;
    oil_unit: string;
    fuel_unit: string;
  };
  attachments: [];
  start_airport: unknown;
  end_airport: unknown;
  callsign: string;
  status: string;
  flights: [
    {
      id: string;
      time_offblocks: string;
      time_onblocks: string;
      time_takeoff: string;
      time_landing: string;
      status: string;
      departure_airport: unknown;
      arrival_airport: unknown;
      fuel_uplift: string;
      landings: number;
      flight_seconds: number;
      fuel_used: string;
      captain: string;
      first_officer: string;
      oil_uplift: {};
    },
    {
      id: string;
      time_offblocks: string;
      time_onblocks: string;
      time_takeoff: string;
      time_landing: string;
      status: string;
      departure_airport: unknown;
      arrival_airport: unknown;
      fuel_uplift: string;
      landings: number;
      flight_seconds: number;
      fuel_used: string;
      captain: string;
      first_officer: string;
      oil_uplift: {
        unit: string;
        engine_1: number;
        engine_2: number;
      };
    },
    {
      id: string;
      time_offblocks: string;
      time_onblocks: string;
      time_takeoff: string;
      time_landing: string;
      status: string;
      departure_airport: unknown;
      arrival_airport: unknown;
      fuel_uplift: string;
      landings: number;
      flight_seconds: number;
      fuel_used: string;
      captain: string;
      first_officer: string;
      oil_uplift: {};
    },
  ];
  captain: {
    id: string;
    last_updated: number;
    first_name: string;
    last_name: string;
    position: string;
    email: string;
    type: string;
    role: string;
    avatar_url: unknown;
    organisation: {
      id: string;
      name: string;
      address: string;
      aoc_number: string;
      date_format: string;
      ddl_template: unknown;
      logo_url: unknown;
      dark_logo_url: unknown;
      custom_signoff_text: {};
    };
  };
  first_officer: {
    id: string;
    last_updated: number;
    first_name: string;
    last_name: string;
    position: string;
    email: string;
    type: string;
    role: string;
    avatar_url: unknown;
    organisation: {
      id: string;
      name: string;
      address: string;
      aoc_number: string;
      date_format: string;
      ddl_template: unknown;
      logo_url: unknown;
      dark_logo_url: unknown;
      custom_signoff_text: {};
    };
  };
}
